/* eslint-disable react-native/no-inline-styles */
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';
import VideoPlayer, {
  PlayerTypeEnum,
} from 'design-system/src/Widget/Player/VideoPlayer/VideoPlayer.web';
import useVideoPlayerLogic, {VideoPlayerParams} from './useVideoPlayerLogic';
import {CustomActivityIndicator} from 'design-system/src/Components/CustomActivityIndicator';
import useParams from '@src/Hooks/useParams';
import PaywallChecker from '@src/Umbraco/Components/PaywallChecker/PaywallChecker';
import useViewPort from 'design-system/src/Hooks/useViewPort';
import useMember from '@src/Store/selectors/useMember';
import {EQuality} from '@src/Navigation/Profile/ProfileSetting/ProfileSetting';
import {usePaywallAccess} from '@src/Providers/PaywallProvider/PaywallProvider';
import useAppLocation from '@src/App/Route/AppRouterContext/useAppLocation';
import {
  IGetMainContentListByIdQuery,
  IPaywallFragmentFragment,
  useGetMainContentListByIdLazyQuery,
} from '@src/Api/Graphql/gql/types.generated';
import PlayerPaywall from '@src/Umbraco/Components/PlayerPaywall/PlayerPaywall';
import {useTranslation} from 'gatsby-plugin-react-i18next';
import useAppVideoPlayerStorage from './useAppVideoPlayerStorage';
import {RouteTypename} from '@src/Navigation/types/route.type';
// Apply the suggested edit: Add the following imports
import VideoErrorDisplay from './components/VideoErrorDisplay/VideoErrorDisplay';
import {LiveEventInfos} from './interfaces/liveEventInfos.interface';
import {PlayerPaywallTrackingProps} from '@src/Services/Amplitude/hooks/tracker';
import {WatchSource} from '@src/Navigation/types/watchPageRouteParams.type';
import {
  extractVideoParamsFromUmbraco,
  ExtractedVideoParams,
} from './utils/extractVideoParamsFromUmbraco';

type ContentItem = NonNullable<
  IGetMainContentListByIdQuery['allContent']
>['items'][number];

const getCoverUrlFromItem = (item: ContentItem) => {
  const covers: Array<{
    __typename: string;
    content?: {desktopImage?: {url?: string}};
  }> = (item && 'cover' in item && (item as any).cover) || [];
  const coverObj = covers.find(cover => cover.__typename === 'BlockListItem');

  const coverUrl = coverObj?.content?.desktopImage?.url ?? '';
  return coverUrl;
};

const getEventInfos = (item?: ContentItem): LiveEventInfos => {
  if (!item) {
    return {description: '', title: '', endDate: '', startDate: ''};
  }

  const {
    description = '',
    title = '',
    endDate = '',
    startDate = '',
  } = item as Partial<Record<keyof LiveEventInfos, string>>;

  return {
    description,
    title,
    endDate,
    startDate,
  };
};

interface AppVideoPlayerProps {
  extractedParams?: ExtractedVideoParams | null;
}

const AppVideoPlayer: FC<AppVideoPlayerProps> = ({
  extractedParams: propExtractedParams,
}) => {
  const location = useAppLocation();
  const [videoPlayerArgs, setVideoPlayerArgs] = useAppVideoPlayerStorage();
  const locationParams: any = useParams();
  const [fetchContent, {loading: getContentLoading}] =
    useGetMainContentListByIdLazyQuery();
  const member = useMember();
  const {t} = useTranslation('translation');
  const [umbData, setUmbData] = useState<IGetMainContentListByIdQuery>();
  const [coverUrl, setCoverUrl] = useState('');
  const [eventInfos, setEventInfos] = useState<LiveEventInfos>({
    description: '',
    title: '',
  });
  const [cooldown, setCooldown] = useState(false);
  const [cooldownLeft, setCooldownLeft] = useState(0);
  const [watchTime, setWatchTime] = useState(0);
  const [extractedParams, setExtractedParams] =
    useState<ExtractedVideoParams | null>(null);
  const [umbracoLoading, setUmbracoLoading] = useState(false);

  // Utiliser les paramètres passés en prop en priorité, sinon lire du localStorage UNIQUEMENT si même ID
  useEffect(() => {
    if (propExtractedParams) {
      setExtractedParams(propExtractedParams);
    } else {
      const savedParams = localStorage.getItem('v-params');
      if (savedParams && locationParams?.id) {
        try {
          const parsed = JSON.parse(savedParams);
          if (parsed.id === locationParams.id) {
            // Vérifier que les params ne sont pas trop vieux (plus de 5 minutes)
            const isRecent =
              parsed.timestamp && Date.now() - parsed.timestamp < 5 * 60 * 1000;
            if (isRecent) {
              setExtractedParams(parsed);
            } else {
              // Si trop vieux mais même ID, utiliser sans timestamp
              const {timestamp, ...paramsWithoutTimestamp} = parsed;
              setExtractedParams(paramsWithoutTimestamp);
            }
          }
        } catch (error) {
          console.error('Error parsing saved params:', error);
        }
      }
    }
  }, [propExtractedParams, locationParams?.id]);

  // Utiliser les paramètres extraits d'Umbraco en priorité, fallback vers locationParams (pas localStorage pour éviter anciens)
  const effectiveParams = extractedParams || locationParams;
  const contentType: RouteTypename = effectiveParams.__typename;
  const paramsSource =
    extractedParams?.source ||
    (contentType === 'LiveEvent' || contentType === 'Channel'
      ? WatchSource.LIVESTREAM
      : effectiveParams?.byteplusId
        ? WatchSource.BYTEPLUS
        : effectiveParams?.source || WatchSource.VIMEO);

  const {canSeeContent, getPremiumPreviewSettings} = usePaywallAccess();
  const videoPlayerInfos = useVideoPlayerLogic({
    ...(effectiveParams || {}),
    paywallData: effectiveParams?.paywallData ||
      extractedParams?.paywallData || {
        accessType: effectiveParams?.accessType,
        paywall: effectiveParams?.paywall,
      },
    source: paramsSource,
    // Forcer le byteplusId si on l'a extrait
    byteplusId: extractedParams?.byteplusId || effectiveParams?.byteplusId,
    // Forcer le titre extrait d'Umbraco en priorité
    title: extractedParams?.title || effectiveParams?.title || '',
  } as VideoPlayerParams);
  const {
    liveEvent,
    isLoading,
    handleOnVideoPlay,
    refCurrentChannel,
    handleOnVideoLoad,
    handleOnGetDuration,
    handleOnBack,
    handleOnPlayEnd,
    handleOnTimeProgress,
    handleOnLanguageChange,
    handleOnSpeedChange,
    handleOnSkipChange,
    onNextEpisode,
    handleOnResolutionChange,
    currentTime,
    displayPaywall,
    paywallSetting,
    content,
    isLive,
    source,
    previewLoading,
  } = videoPlayerInfos;

  const paywallData =
    effectiveParams?.paywallData || extractedParams?.paywallData;

  const currentIndex = refCurrentChannel.current || 0;
  const currentSource = liveEvent?.sourceList?.[currentIndex];
  const {viewportHeight} = useViewPort();
  const playerType: PlayerTypeEnum =
    paramsSource === WatchSource.LIVESTREAM ? 'live' : 'standard';
  const url = (currentSource as any)?.uri || '';
  const poster = (currentSource as any)?.poster || '';
  const accessType =
    (currentSource && currentSource.accessData?.accessType) ||
    paywallData?.accessType;
  const hasAccess: boolean = accessType ? canSeeContent(accessType) : true;
  const canPreview = getPremiumPreviewSettings(
    paywallData?.paywall as IPaywallFragmentFragment,
  )?.canPreview;

  const fetchAndExtractParams = useCallback(async () => {
    if (!locationParams?.id) return;

    setUmbracoLoading(true);
    try {
      const {data: contentData} = await fetchContent({
        variables: {id: [locationParams.id]},
      });

      if (contentData) {
        setUmbData(contentData);

        // Extraire les paramètres vidéo depuis Umbraco
        const extracted = extractVideoParamsFromUmbraco(
          contentData,
          locationParams.id,
        );
        if (extracted) {
          setExtractedParams(extracted);

          // Sauvegarder en localStorage avec timestamp pour cache
          setVideoPlayerArgs({
            ...extracted,
            id: locationParams.id,
            timestamp: Date.now(),
          });
        }
      }
    } finally {
      setUmbracoLoading(false);
    }
  }, [fetchContent, locationParams?.id]);

  const refreshAll = useCallback(() => {
    fetchAndExtractParams();

    setUmbData(undefined);
    setCoverUrl('');
    setEventInfos({description: '', title: '', endDate: '', startDate: ''});
    setCooldown(true);
    setCooldownLeft(60);
  }, [fetchAndExtractParams]);

  useEffect(() => {
    if (!propExtractedParams) {
      fetchAndExtractParams();
    }
  }, [locationParams?.id, propExtractedParams]);

  useEffect(() => {
    if (!cooldown) return;
    if (cooldownLeft <= 0) {
      setCooldown(false);
      return;
    }
    const interval = window.setInterval(() => {
      setCooldownLeft(prev => {
        if (prev <= 1) {
          window.clearInterval(interval);
          setCooldown(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, [cooldown, cooldownLeft]);

  useEffect(() => {
    if (!umbData) return;

    const {allContent} = umbData;
    const items = allContent?.items || [];

    const currentItem: ContentItem | undefined = items.find(
      (item): item is ContentItem => item?.id === locationParams?.id,
    );

    setCoverUrl(getCoverUrlFromItem(currentItem));
    setEventInfos({
      ...getEventInfos(currentItem),
      onRefresh: refreshAll,
    });
  }, [umbData]);

  useEffect(() => {
    if (
      locationParams &&
      ((!!locationParams.liveEventId &&
        locationParams.liveEventId !== videoPlayerArgs.liveEventId) ||
        (!!locationParams.videoId &&
          locationParams.videoId !== videoPlayerArgs.videoId) ||
        (!!locationParams.videoUrl &&
          locationParams.videoUrl !== videoPlayerArgs.videoUrl) ||
        (!!locationParams.sourceUrl &&
          locationParams.sourceUrl !== videoPlayerArgs.sourceUrl))
    ) {
      setVideoPlayerArgs(locationParams);
    }
  }, [locationParams, videoPlayerArgs]);

  useEffect(() => {
    // update submenu left title
    if (currentSource && location) {
      const title = currentSource?.description;
      location.state = {
        ...(location.state || {}),
        title: title,
        params: {
          ...((location.state as any)?.params || {}),
          title: title,
        },
      };
    }
  }, [currentSource, location]);

  const maxPreview = isLive
    ? paywallSetting.previewDurationTv
    : paywallSetting.previewDurationVod;
  const paywallCheckerTrackingData: PlayerPaywallTrackingProps = useMemo(() => {
    if (!content) {
      return {
        maxPreviewDurationMinutes: 0,
        watchedTimeSeconds3Decimals: watchTime,
        id: 'VideoPlayerDefaultContentTracking',
        title: 'VideoPlayerDefaultContentTracking',
        __typename: 'VODItem',
        isTrackingReady: false,
        source_url: window.location.href,
      };
    }

    const {id, title, __typename} = content;

    return {
      maxPreviewDurationMinutes: maxPreview,
      watchedTimeSeconds3Decimals: watchTime,
      id,
      title,
      __typename,
      source_url: window.location.href,
    };
  }, [maxPreview, watchTime, content]);

  const onTimeProgress = (seconds: number) => {
    setWatchTime(prev => prev + seconds);
    handleOnTimeProgress && handleOnTimeProgress(seconds);
  };

  if (!url && !isLoading) {
    console.log('Legacy effectiveParams:', effectiveParams);
    console.log('Legacy extractedParams:', extractedParams);
    console.log('Legacy liveEvent:', liveEvent);
    console.log('Legacy sourceList:', liveEvent?.sourceList);
    console.log('Legacy currentSource:', currentSource);
    console.log('Legacy url:', url);
    return (
      <VideoErrorDisplay
        message={t('No video found')}
        viewportHeight={viewportHeight}
        isLiveStream={source === WatchSource.LIVESTREAM}
        posterUrl={coverUrl || poster || ''}
        eventInfos={eventInfos}
        cooldown={cooldown}
        cooldownLeft={cooldownLeft}
      />
    );
  }

  if (umbracoLoading || !extractedParams || isLoading || previewLoading) {
    return (
      <div
        style={{
          width: '100%',
          height: viewportHeight,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
        <CustomActivityIndicator />
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'fixed',
        zIndex: 49,
      }}>
      <PaywallChecker
        accessType={accessType}
        trackingData={paywallCheckerTrackingData}>
        <VideoPlayer
          disableCast={!hasAccess}
          paywallContent={
            <PlayerPaywall
              isLive={isLive || source === WatchSource.LIVESTREAM}
              setting={paywallSetting}
              content={content}
              secondsWatched={watchTime}
              displayPaywall={Boolean(displayPaywall)}
            />
          }
          displayPaywall={displayPaywall}
          sourceList={liveEvent?.sourceList || []}
          title={extractedParams?.title ?? liveEvent?.title ?? ''}
          onNext={onNextEpisode}
          onGoBack={handleOnBack}
          type={playerType}
          poster={poster}
          currentChannel={refCurrentChannel.current}
          onVideoLoad={handleOnVideoLoad}
          onGetDuration={handleOnGetDuration}
          onPlayEnd={handleOnPlayEnd}
          onTimeProgress={onTimeProgress}
          handleOnLanguageChange={handleOnLanguageChange}
          handleOnSpeedChange={handleOnSpeedChange}
          handleOnSkipChange={handleOnSkipChange}
          handleOnResolutionChange={handleOnResolutionChange}
          initialTime={currentTime}
          onSetCurrentChannel={current => {
            refCurrentChannel.current = current;
          }}
          onVideoPlay={handleOnVideoPlay}
          memberVideoQuality={
            (member?.videoQuality || EQuality.AUTO) as EQuality
          }
        />
      </PaywallChecker>
    </div>
  );
};

export default AppVideoPlayer;
